import React, { useState, useCallback } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    Switch,
    FormControlLabel,
    IconButton,
    Divider,
    Chip,
    Autocomplete,
    CircularProgress,
    Alert,
    Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import { useCreateOrgUsersMutation, NewUserPayload } from '../../../shared/queries/admin'

interface Roadmap {
    _id: string
    product: string
    indication: string
}

interface AddUsersDialogProps {
    orgId: string
    open: boolean
    onClose: () => void
    onSuccess: (count: number) => void
    onError: (msg: string) => void
    isAllowedUsersEnabled: boolean
    roadmaps: Roadmap[]
}

interface UserRow {
    id: number
    name: string
    email: string
    readonly: boolean
    commentingReadonly: boolean
    roadmapIds: string[]
    // set after submission
    result?: 'created' | 'failed'
    failReason?: string
}

let nextId = 1
const makeRow = (defaults: Partial<UserRow> = {}): UserRow => ({
    id: nextId++,
    name: '',
    email: '',
    readonly: false,
    commentingReadonly: false,
    roadmapIds: [],
    ...defaults,
})

const AddUsersDialog: React.FC<AddUsersDialogProps> = ({
    orgId,
    open,
    onClose,
    onSuccess,
    onError,
    isAllowedUsersEnabled,
    roadmaps,
}) => {
    const [rows, setRows] = useState<UserRow[]>([makeRow()])
    const [globalReadonly, setGlobalReadonly] = useState(false)
    const [globalCommentingReadonly, setGlobalCommentingReadonly] = useState(false)
    const [saving, setSaving] = useState(false)
    const [submitted, setSubmitted] = useState(false)

    const createUsersMutation = useCreateOrgUsersMutation()

    const roadmapById = Object.fromEntries(roadmaps.map((r) => [r._id, r]))

    const updateRow = useCallback((id: number, patch: Partial<UserRow>) => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)))
    }, [])

    const removeRow = useCallback((id: number) => {
        setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev))
    }, [])

    const addRow = () => {
        setRows((prev) => [
            ...prev,
            makeRow({
                readonly: globalReadonly,
                commentingReadonly: globalCommentingReadonly,
            }),
        ])
    }

    const handleClose = () => {
        if (saving) return
        setRows([makeRow()])
        setGlobalReadonly(false)
        setGlobalCommentingReadonly(false)
        setSubmitted(false)
        onClose()
    }

    const handleSave = async () => {
        const validRows = rows.filter((r) => r.name.trim() && r.email.trim())
        if (validRows.length === 0) return

        setSaving(true)
        try {
            const payload: NewUserPayload[] = validRows.map((r) => ({
                name: r.name.trim(),
                email: r.email.trim().toLowerCase(),
                readonly: r.readonly,
                commentingReadonly: r.commentingReadonly,
                roadmapIds: r.roadmapIds,
            }))

            const res = await createUsersMutation.mutateAsync({ orgId, users: payload })
            const { created, failed } = res.data

            // Annotate rows with results
            setRows((prev) =>
                prev.map((row) => {
                    const emailNorm = row.email.trim().toLowerCase()
                    const wasCreated = created.find((c: any) => c.email === emailNorm)
                    const wasFailed = failed.find((f: any) => f.email === emailNorm)
                    if (wasCreated) return { ...row, result: 'created' }
                    if (wasFailed) return { ...row, result: 'failed', failReason: wasFailed.reason }
                    return row
                })
            )

            setSubmitted(true)
            if (created.length > 0) {
                onSuccess(created.length)
            }
            if (created.length === 0 && failed.length > 0) {
                onError('All users failed to create — see details below')
            }
        } catch (err) {
            onError(err?.message || 'Failed to create users')
        } finally {
            setSaving(false)
        }
    }

    const readyRows = rows.filter((r) => r.name.trim() && r.email.trim())
    const hasResults = rows.some((r) => r.result)
    const allDone = submitted && rows.every((r) => !r.name.trim() || !r.email.trim() || r.result)

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAddIcon />
                Add Users to Organization
            </DialogTitle>

            <DialogContent dividers>
                {/* Global defaults */}
                <Box
                    sx={{
                        mb: 3,
                        p: 2,
                        bgcolor: 'figma.grayscale.table1',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'figma.grayscale.stroke',
                    }}
                >
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                        Default permissions for new rows
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={globalReadonly}
                                    onChange={(e) => setGlobalReadonly(e.target.checked)}
                                />
                            }
                            label="Read-only"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={globalCommentingReadonly}
                                    onChange={(e) => setGlobalCommentingReadonly(e.target.checked)}
                                />
                            }
                            label="Commenting read-only"
                        />
                    </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* User rows */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {rows.map((row, idx) => (
                        <Box
                            key={row.id}
                            sx={{
                                p: 1.5,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor:
                                    row.result === 'created'
                                        ? 'success.light'
                                        : row.result === 'failed'
                                          ? 'error.light'
                                          : 'divider',
                                bgcolor:
                                    row.result === 'created'
                                        ? 'success.50'
                                        : row.result === 'failed'
                                          ? 'error.50'
                                          : 'background.paper',
                            }}
                        >
                            {/* Top row: number, name, email, status/delete */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                <Typography
                                    variant="caption"
                                    sx={{ minWidth: 20, color: 'text.secondary', fontWeight: 600 }}
                                >
                                    {idx + 1}
                                </Typography>
                                <TextField
                                    size="small"
                                    label="Name"
                                    value={row.name}
                                    onChange={(e) => updateRow(row.id, { name: e.target.value })}
                                    sx={{ flex: 1 }}
                                    disabled={saving || !!row.result}
                                />
                                <TextField
                                    size="small"
                                    label="Email"
                                    type="email"
                                    value={row.email}
                                    onChange={(e) => updateRow(row.id, { email: e.target.value })}
                                    sx={{ flex: 1 }}
                                    disabled={saving || !!row.result}
                                />
                                {row.result === 'created' ? (
                                    <Tooltip title="Created successfully">
                                        <CheckCircleOutlineIcon color="success" />
                                    </Tooltip>
                                ) : row.result === 'failed' ? (
                                    <Tooltip title={row.failReason || 'Failed'}>
                                        <ErrorOutlineIcon color="error" />
                                    </Tooltip>
                                ) : (
                                    <IconButton
                                        size="small"
                                        onClick={() => removeRow(row.id)}
                                        disabled={saving || rows.length === 1}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>

                            {/* Bottom row: permissions + roadmap selector */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: '28px' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={row.readonly}
                                            onChange={(e) => updateRow(row.id, { readonly: e.target.checked })}
                                            disabled={saving || !!row.result}
                                        />
                                    }
                                    label={<Typography variant="caption">Read-only</Typography>}
                                    sx={{ m: 0 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={row.commentingReadonly}
                                            onChange={(e) =>
                                                updateRow(row.id, { commentingReadonly: e.target.checked })
                                            }
                                            disabled={saving || !!row.result}
                                        />
                                    }
                                    label={<Typography variant="caption">Commenting read-only</Typography>}
                                    sx={{ m: 0 }}
                                />
                                {isAllowedUsersEnabled && roadmaps.length > 0 && (
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={roadmaps}
                                        getOptionLabel={(r) => `${r.product} — ${r.indication}`}
                                        value={roadmaps.filter((r) => row.roadmapIds.includes(r._id))}
                                        onChange={(_, selected) =>
                                            updateRow(row.id, { roadmapIds: selected.map((r) => r._id) })
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Roadmap access"
                                                placeholder="Select roadmaps…"
                                            />
                                        )}
                                        sx={{ flex: 1 }}
                                        disabled={saving || !!row.result}
                                        limitTags={4}
                                    />
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>

                {/* Failure details */}
                {hasResults && rows.some((r) => r.result === 'failed') && (
                    <Box sx={{ mt: 2 }}>
                        {rows
                            .filter((r) => r.result === 'failed')
                            .map((r) => (
                                <Alert key={r.id} severity="error" sx={{ mb: 1 }}>
                                    <strong>{r.email}</strong>: {r.failReason}
                                </Alert>
                            ))}
                    </Box>
                )}

                {/* Add row button */}
                {!submitted && (
                    <Box sx={{ mt: 2 }}>
                        <Button
                            startIcon={<AddIcon />}
                            onClick={addRow}
                            disabled={saving}
                            size="small"
                            variant="outlined"
                        >
                            Add another user
                        </Button>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                {readyRows.length > 0 && !submitted && (
                    <Chip label={`${readyRows.length} user${readyRows.length > 1 ? 's' : ''} to create`} size="small" />
                )}
                <Box sx={{ flex: 1 }} />
                <Button onClick={handleClose} disabled={saving}>
                    {allDone ? 'Close' : 'Cancel'}
                </Button>
                {!allDone && (
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving || readyRows.length === 0}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {saving
                            ? 'Creating…'
                            : `Create ${readyRows.length > 0 ? readyRows.length : ''} User${readyRows.length !== 1 ? 's' : ''}`}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default AddUsersDialog
